/** Per-user keyboard shortcut overrides for the image editor
 *  (Tier 3 Group C Phase 2). Persists the override map that the
 *  Settings → Keyboard panel writes; EditorPage reads it at mount
 *  and pushes it into the command registry via
 *  `registry.setAllShortcutOverrides(loaded)`.
 *
 *  Storage shape — Record<commandId, CommandShortcut[]>:
 *    `{ "edit.undo": [{ "key": "z", "mod": true }], ... }`
 *  Versioned storage key (`:v1`) keeps a future schema bump
 *  compatible — if the override shape grows fields, bump to `:v2`
 *  and leave `:v1` orphaned in storage.
 *
 *  Defensive parsing: bad JSON, wrong shape, or unknown field
 *  types all degrade to "no overrides" rather than throw — the
 *  command registry's defaults stay in effect. A broken override
 *  list is a degraded experience, never a blocker.
 *
 *  Built on `lib/user-prefs-storage.ts`'s `createPersistedNamespace`
 *  so the safe-storage + defensive-parse plumbing is shared with
 *  every other persisted preference (palette recents, future
 *  history-cap setting, etc.). */

import type { CommandShortcut } from './command-registry';
import { createPersistedNamespace } from './user-prefs-storage';

export type KeyboardOverrides = Record<string, readonly CommandShortcut[]>;

const namespace = createPersistedNamespace<KeyboardOverrides>({
  storageKey: 'image-editor:keyboard-overrides:v1',
  defaultValue: {},
  parse: (raw): KeyboardOverrides => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
    const out: Record<string, readonly CommandShortcut[]> = {};
    for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof id !== 'string' || id.length === 0) continue;
      if (!Array.isArray(value)) continue;
      const shortcuts = value.map(coerceShortcut).filter((s): s is CommandShortcut => s !== null);
      // Empty array IS valid — means "explicitly unbind this
      // command." Don't drop the entry.
      out[id] = shortcuts;
    }
    return out;
  },
});

/** Read the persisted overrides. Always returns a plain object;
 *  bad / missing / malformed data parses as `{}` (no overrides). */
export const loadKeyboardOverrides = namespace.load;

/** Persist the override map. Pass `{}` to clear all overrides
 *  (registry still needs `clearShortcutOverrides()` to take
 *  effect at runtime — this only writes the storage). */
export const saveKeyboardOverrides = namespace.save;

/** Drop the persisted entry entirely. Equivalent to
 *  `saveKeyboardOverrides({})` in effect; preferred when the user
 *  hits "Reset All" so the storage entry doesn't linger as `{}`. */
export const clearKeyboardOverrides = namespace.clear;

/** Exported for tests that want to assert the storage key (and
 *  for the export/import JSON UI, which uses the same shape on
 *  disk as in storage). */
export const KEYBOARD_OVERRIDES_STORAGE_KEY = namespace.storageKey;

/** Defensive coercion of a single shortcut entry. Returns null
 *  for entries that don't shape-match the CommandShortcut
 *  interface. Boolean fields are optional and default to false. */
function coerceShortcut(value: unknown): CommandShortcut | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (typeof v.key !== 'string' || v.key.length === 0) return null;
  const out: CommandShortcut = { key: v.key };
  if (typeof v.code === 'string') out.code = v.code;
  if (v.mod === true) out.mod = true;
  if (v.ctrl === true) out.ctrl = true;
  if (v.shift === true) out.shift = true;
  if (v.alt === true) out.alt = true;
  return out;
}
